import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import s from './dashboard-bottombar.module.scss'
import { NavLink, useLocation } from 'react-router-dom'
import { IconHistory, IconHome, IconMessage, IconMessages, IconSearch, IconUser, IconUsersPlus, IconReceipt, IconDots } from '@tabler/icons-react'
import classNames from 'classnames';
import { useProfile } from '@shared/api/useProfile';
import { useChatStore } from '@entities/messanger/model/chat.store';
import { useClickOutside } from '@utils/lib/useClickOutside';

const studentItems = [
	{ to: '/', label: 'Главная', icon: IconHome },
	{ to: '/messenger', label: 'Чаты', icon: IconMessage, messenger: true },
	{ to: '/teachers', label: 'Заниматься', icon: IconSearch },
	{ to: '/history', label: 'Занятия', icon: IconHistory },
	{ to: '/settings', label: 'Профиль', icon: IconUser },
];

const teacherItems = [
	{ to: '/', label: 'Главная', icon: IconHome },
	{ to: '/messenger', label: 'Чаты', icon: IconMessages, messenger: true },
	{ to: '/history', label: 'Занятия', icon: IconHistory },
	{ to: '/referrals', label: 'Рефералы', icon: IconUsersPlus },
	{ to: '/payment-history', label: 'Выплаты', icon: IconReceipt },
	{ to: '/settings', label: 'Профиль', icon: IconUser },
];

const MenuLink = ({ item, isActive, className, onClick }) => {
	const Icon = item.icon;
	return (
		<NavLink
			to={item.to}
			className={classNames(s.menuLink, isActive ? s.active : s.inactive, className)}
			onClick={onClick}
		>
			<Icon size={24} />
			{isActive && <span>{item.label}</span>}
		</NavLink>
	);
};

const MoreMenuLink = ({ item, isActive, onClick }) => {
	const Icon = item.icon;
	return (
		<NavLink
			to={item.to}
			className={classNames(s.moreMenuLink, isActive && s.moreMenuActive)}
			onClick={onClick}
		>
			<Icon size={20} />
			<span>{item.label}</span>
		</NavLink>
	);
};

export const DashboardBottombar = () => {
	const { data: profile } = useProfile();
	const chats = useChatStore((state) => state.chats);
	const location = useLocation();
	const [visibleCount, setVisibleCount] = useState(5);
	const [isMoreOpen, setIsMoreOpen] = useState(false);
	const wrapperRef = useRef(null);
	const measureListRef = useRef(null);
	const itemMeasureRefs = useRef([]);
	const moreMeasureRef = useRef(null);
	const moreRef = useRef(null);

	useClickOutside(moreRef, () => setIsMoreOpen(false));

	const items = profile?.role === 'student' ? studentItems : teacherItems;

	const isActive = useCallback((to) => {
		if (to === '/') return location.pathname === '/';
		return location.pathname.startsWith(to);
	}, [location.pathname]);

	const hasUnread = chats?.findIndex(
		(_) => _?.last_message && _?.last_message?.sender_id !== profile?.id && !_?.last_message?.is_read
	) !== -1;

	const measure = useCallback(() => {
		if (!wrapperRef.current || !measureListRef.current) return;

		const containerWidth = wrapperRef.current.clientWidth;
		const itemWidths = itemMeasureRefs.current.map((el) => el ? el.getBoundingClientRect().width : 0);
		const moreWidth = moreMeasureRef.current ? moreMeasureRef.current.getBoundingClientRect().width : 0;

		const totalWithoutMore = itemWidths.reduce((a, b) => a + b, 0) + ((itemWidths?.length - 1) * 8) + 40;
		let newCount = items.length;

		if (totalWithoutMore > containerWidth) {
			for (let K = items.length - 1; K >= 0; K--) {
				const width = itemWidths.slice(0, K).reduce((a, b) => a + b, 0)  + ((itemWidths?.length - 1) * 8) + 40 + moreWidth;
				if (width <= containerWidth) {
					newCount = K;
					break;
				}
			}
		}

		setVisibleCount((prev) => (prev === newCount ? prev : newCount));
	}, [items]);

	useLayoutEffect(() => {
		measure();
	}, [measure]);

	useEffect(() => {
		const ro = new ResizeObserver(measure);
		if (wrapperRef.current) ro.observe(wrapperRef.current);
		return () => ro.disconnect();
	}, [measure]);

	useEffect(() => {
		measure();
	}, [location.pathname, measure]);

	useEffect(() => {
		setIsMoreOpen(false);
	}, [location.pathname]);

	if (/\/appointment\//.test(window.location.href)) {
		return null;
	}

	const visibleItems = items.slice(0, visibleCount);
	const hiddenItems = items.slice(visibleCount);
	const showMore = hiddenItems.length > 0;

	return (
		<div className={s.wrapper} ref={wrapperRef}>
			<ul className={s.menuList}>
				{visibleItems.map((item, index) => (
					<li
						key={item.to}
						className={classNames(
							s.menuItem,
							item.messenger && s.messenger,
							item.messenger && hasUnread && s.unread
						)}
					>
						<MenuLink item={item} isActive={isActive(item.to)} />
					</li>
				))}
				{showMore && (
					<li className={classNames(s.menuItem, s.more)} ref={moreRef}>
						<button
							className={classNames(s.menuLink, s.inactive, isMoreOpen && s.moreActive)}
							onClick={() => setIsMoreOpen((prev) => !prev)}
							type="button"
						>
							<IconDots size={24} />
						</button>
						{isMoreOpen && (
							<div className={s.moreMenu}>
								{hiddenItems.map((item) => (
									<MoreMenuLink
										key={item.to}
										item={item}
										isActive={isActive(item.to)}
										onClick={() => setIsMoreOpen(false)}
									/>
								))}
							</div>
						)}
					</li>
				)}
			</ul>

			<ul className={s.measureList} ref={measureListRef} aria-hidden="true">
				{items.map((item, index) => (
					<li
						key={item.to}
						className={classNames(
							s.menuItem,
							item.messenger && s.messenger,
							item.messenger && hasUnread && s.unread
						)}
						ref={(el) => { itemMeasureRefs.current[index] = el; }}
					>
						<MenuLink item={item} isActive={isActive(item.to)} />
					</li>
				))}
				<li className={classNames(s.menuItem, s.more)} ref={moreMeasureRef}>
					<button className={classNames(s.menuLink, s.inactive)} type="button">
						<IconDots size={24} />
					</button>
				</li>
			</ul>
		</div>
	);
};
